/**
 * Connecting a Codex account: the access-mode choice, the account rows, and the
 * device-code dialog that walks someone through signing in.
 *
 * Deliberately one component rather than several, which is worth explaining
 * because it is over the 300-line guidance in
 * `.agents/rules/frontend-architecture.md`.
 *
 * Every seam inside it was measured before this file existed, and all of them
 * are shallow: extracting the dialog needs 44 of the component's own locals as
 * props for 334 lines of JSX; extracting the access-mode block needs 18 for
 * 130; splitting the hooks from the render needs 53. An interface nearly as
 * wide as the implementation it hides is not a module -- it is the same
 * coupling with a file boundary drawn through it, and it would make the auth
 * flow harder to follow while satisfying a line count.
 *
 * What made the original file a problem was that it mixed this flow with the
 * Claude flow, the provider cards, the summary logic and the copy tables. Those
 * had real seams and have been taken out.
 *
 * @module
 */
import { CmmdCodexCredentialState, cmmdApiClient } from "../../environments/cmmdApi";
import { getPrimaryEnvironmentConnection } from "../../environments/runtime";
import {
  CloudProviderSignInController,
  isCloudProviderSignInEnabled,
  providerAccountsQueryKey,
} from "../../hooks/useCloudProviderSignIn";
import { maskConnectedAccountLabel } from "../../lib/connectedAccountEmail";
import { cn } from "../../lib/utils";
import { ensureLocalApi } from "../../localApi";
import { getProviderAuthFailureTelemetry } from "../../providerAuthFailureTelemetry";
import { applyProvidersUpdated } from "../../rpc/serverState";
import { captureWebError } from "../../sentry";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { toastManager } from "../ui/toast";
import { CloudProviderSignInView } from "./CloudProviderSignInSection";
import {
  ProviderAccountListItem,
  ProviderAccountManagePanel,
  ProviderAccountSelector,
} from "./ProviderAccountManagePanel";
import {
  CodexAuthManualCopyValues,
  formatCodexAuthStartError,
  getCodexAuthAccessSourceViewModel,
  getCodexAuthFailureViewModel,
  shouldUseCodexCloudSignIn,
} from "./codexAuthViewModel";
import {
  CmmdProviderAccountInventoryAccount,
  resolveCodexPersonalSelectionState,
  resolveProviderAccountRowSource,
  resolveProviderAccountRows,
} from "./providerAccountState";
import { CODEX_AUTH_BROWSER_THREAD_ID, CODEX_DEVICE_AUTH_HELP_URL } from "./providerAuthCopy";
import { copyProviderAuthText, isProviderAuthSessionTerminal } from "./providerProvenance";
import { ProviderAuthSession, ServerProvider } from "@cmmd-forge/contracts";
import { UnifiedSettings } from "@cmmd-forge/contracts/settings";
import { useQueryClient } from "@tanstack/react-query";
import { CopyIcon, ExternalLinkIcon, InfoIcon, LoaderIcon, RefreshCwIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

// moved verbatim from the 5377-line SettingsPanels.tsx; complexity is
// unchanged, and fallow reads a relocated function as a new one
// fallow-ignore-next-line complexity
export function CodexAuthenticationOptions({
  provider,
  accessMode,
  onAccessModeChange,
  onAuthCompleted,
  cloudSignIn,
  providerAccounts,
  workspaceWalletAllowed = true,
}: {
  provider: ServerProvider | undefined;
  accessMode: UnifiedSettings["providers"]["codex"]["accessMode"];
  onAccessModeChange: (mode: UnifiedSettings["providers"]["codex"]["accessMode"]) => void;
  onAuthCompleted: () => void;
  cloudSignIn: CloudProviderSignInController<CmmdCodexCredentialState>;
  providerAccounts: ReadonlyArray<CmmdProviderAccountInventoryAccount>;
  /** When false (personal-only org policy), hide/disable the workspace radio. */
  readonly workspaceWalletAllowed?: boolean;
}) {
  const [authSession, setAuthSession] = useState<ProviderAuthSession | null>(null);
  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false);
  const [isStartingAuth, setIsStartingAuth] = useState(false);
  const [isRepairingAuthRuntime, setIsRepairingAuthRuntime] = useState(false);
  const [isDisconnectingAuth, setIsDisconnectingAuth] = useState(false);
  const [authBrowserError, setAuthBrowserError] = useState<string | null>(null);
  const [manualCopyValues, setManualCopyValues] = useState<CodexAuthManualCopyValues>({});
  const [selectPersonalAfterCloudAuth, setSelectPersonalAfterCloudAuth] = useState(false);
  const [selectingCodexDefaultAccountId, setSelectingCodexDefaultAccountId] = useState<
    string | null
  >(null);
  const queryClient = useQueryClient();
  // "Switch account" / "Reconnect" start a new device-auth run while an account
  // is still connected. Without this the dialog would keep rendering the connected
  // panel and the sign-in code would never be shown.
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [reconnectTarget, setReconnectTarget] = useState<"primary" | "linked">("primary");
  const [removingAccountId, setRemovingAccountId] = useState<string | null>(null);
  const hasHandledAuthCompletionRef = useRef(false);
  const openedAuthSessionRef = useRef<string | null>(null);
  const reportedAuthFailureSessionRef = useRef<string | null>(null);
  const codexCloudSignInAvailable = isCloudProviderSignInEnabled();
  const providerAccountsForCodexSurface = codexCloudSignInAvailable ? providerAccounts : [];
  const cloudCredential = codexCloudSignInAvailable ? cloudSignIn.connection : null;
  const {
    codexAccountEmail,
    codexCredentialScopeLabel,
    codexPlanLabel,
    defaultScopeLabel,
    hasUserCodexAccount,
    connectedViaCloudStore,
    needsReconnect,
    isAuthFailed,
    isAuthSessionActive,
    isAuthVerifying,
    isUsingChatGptAccount,
    isWorkspaceOnline,
    normalizedUserCode,
    personalAccessDescription,
    workspaceAccessDescription,
  } = getCodexAuthAccessSourceViewModel({
    provider,
    accessMode,
    authSession,
    cloudCredential,
    cloudSignInAvailable: codexCloudSignInAvailable,
  });
  const hasUsableCodexProviderAccount = providerAccountsForCodexSurface.some(
    (account) => !account.needsReconnect,
  );
  const hasManageableCodexAccount =
    hasUserCodexAccount || providerAccountsForCodexSurface.length > 0;
  const codexAccountRowProviderAccounts = resolveProviderAccountRowSource({
    providerAccounts: providerAccountsForCodexSurface,
    connectedViaCloudStore,
    hasLocalAccount: hasUserCodexAccount,
  });
  const codexAccountRowsAreCloudBacked = codexAccountRowProviderAccounts.length > 0;
  const shouldUseCodexCloudAccountFlow =
    codexCloudSignInAvailable && (connectedViaCloudStore || codexAccountRowsAreCloudBacked);
  const disconnectsCodexCloudStore =
    connectedViaCloudStore || (!hasUserCodexAccount && codexAccountRowsAreCloudBacked);
  const isManagingConnectedAccount = hasManageableCodexAccount && !isReconnecting;
  const readyAuthSession = authSession?.state === "waiting_for_user" ? authSession : null;
  const authFailureViewModel = getCodexAuthFailureViewModel(authSession);
  const useCodexCloudSignIn = shouldUseCodexCloudSignIn({
    authSession,
    cloudSignInAvailable: codexCloudSignInAvailable,
  });
  const isCodexCloudSwitching = isReconnecting && shouldUseCodexCloudAccountFlow;
  const {
    workspaceSelected: effectiveIsUsingWorkspacePlan,
    personalSelected: effectiveIsUsingChatGptAccount,
  } = resolveCodexPersonalSelectionState({
    accessMode,
    workspaceWalletAllowed,
    isUsingChatGptAccount,
    hasUsableProviderAccount: hasUsableCodexProviderAccount,
    hasLocalAccount: hasUserCodexAccount,
  });
  const codexAccountRows = resolveProviderAccountRows({
    providerAccounts: codexAccountRowProviderAccounts,
    accountEmail: codexAccountEmail,
    planLabel: codexPlanLabel,
    isDefaultForNewChats: effectiveIsUsingChatGptAccount,
    needsReconnect,
  });
  const showInlineCodexAccounts = codexAccountRows.length > 1;

  useEffect(() => {
    if (!workspaceWalletAllowed && accessMode === "workspace") {
      onAccessModeChange("personal");
    }
  }, [accessMode, onAccessModeChange, workspaceWalletAllowed]);

  useEffect(() => {
    if (isReconnecting && cloudSignIn.phase?.kind === "done") {
      if (selectPersonalAfterCloudAuth) {
        onAccessModeChange("personal");
        setSelectPersonalAfterCloudAuth(false);
      }
      setIsReconnecting(false);
    }
  }, [cloudSignIn.phase?.kind, isReconnecting, onAccessModeChange, selectPersonalAfterCloudAuth]);

  const openCodexAuthInBrowser = useCallback(async (url: string) => {
    const primaryConnection = getPrimaryEnvironmentConnection();
    const environmentId = primaryConnection.environmentId;
    const browserApi = primaryConnection.client.browser;
    if (!browserApi) {
      throw new Error("The in-app browser controller is not available.");
    }
    const existing = await browserApi.getSession({
      threadId: CODEX_AUTH_BROWSER_THREAD_ID,
      environmentId,
    });
    if (existing) {
      await browserApi.command({
        threadId: CODEX_AUTH_BROWSER_THREAD_ID,
        environmentId,
        browserId: existing.browserId,
        command: { type: "setVisibility", visibility: "visible" },
      });
      await browserApi.command({
        threadId: CODEX_AUTH_BROWSER_THREAD_ID,
        environmentId,
        browserId: existing.browserId,
        command: { type: "navigate", url },
      });
      return;
    }
    await browserApi.openSession({
      threadId: CODEX_AUTH_BROWSER_THREAD_ID,
      environmentId,
      name: "Codex account sign-in",
      ownerRole: "user",
      visibility: "visible",
      initialUrl: url,
    });
  }, []);

  const openCodexAuthFromUserAction = useCallback(
    async (url: string) => {
      await openCodexAuthInBrowser(url);
    },
    [openCodexAuthInBrowser],
  );

  /**
   * `target` defaults to "primary" because every DIRECT entry point here is a
   * primary sign-in. reconnectTarget is sticky, so without resetting it an
   * earlier "Add account" attempt would leave it on "linked" and a later
   * primary Connect - e.g. reconnecting after a disconnect - would skip
   * switching access mode to personal and POST the credential as yet another
   * linked account instead of restoring the primary connection.
   *
   * startReconnectCodexAuth passes its own target through rather than relying
   * on the state it just set: both setState calls land in one batch, so an
   * unconditional reset here would clobber "linked" and silently turn every
   * local-surface "Add account" back into an overwrite of the primary
   * (FOR-2605).
   */
  const startCodexAuth = useCallback(async (target: "primary" | "linked" = "primary") => {
    setReconnectTarget(target);
    setIsStartingAuth(true);
    setIsAuthDialogOpen(true);
    setAuthBrowserError(null);
    setManualCopyValues({});
    openedAuthSessionRef.current = null;
    hasHandledAuthCompletionRef.current = false;
    try {
      const session = await ensureLocalApi().server.startProviderAuth({ provider: "codex" });
      setAuthSession(session);
    } catch (error) {
      const message = formatCodexAuthStartError(error);
      setAuthSession({
        id: `provider-auth:codex:client-error:${Date.now()}` as ProviderAuthSession["id"],
        provider: "codex",
        state: "failed",
        startedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        message,
      });
    } finally {
      setIsStartingAuth(false);
    }
  }, []);

  /**
   * Sign in again while an account is already connected — the recovery path for
   * "which account is this?" as well as a deliberate account switch. The existing
   * credential stays until the new sign-in completes, so a cancelled switch leaves
   * the user exactly where they were.
   */
  const startReconnectCodexAuth = useCallback(
    async (options?: {
      readonly selectPersonalOnSuccess?: boolean;
      readonly credentialTarget?: "primary" | "linked";
    }) => {
      setIsReconnecting(true);
      setSelectPersonalAfterCloudAuth(options?.selectPersonalOnSuccess === true);
      setAuthSession(null);
      if (shouldUseCodexCloudAccountFlow) {
        cloudSignIn.start("codex", { credentialTarget: options?.credentialTarget });
        setIsAuthDialogOpen(true);
        return;
      }
      await startCodexAuth(options?.credentialTarget ?? "primary");
    },
    [cloudSignIn, shouldUseCodexCloudAccountFlow, startCodexAuth],
  );

  const repairAndRetryCodexAuth = useCallback(async () => {
    setIsRepairingAuthRuntime(true);
    try {
      const repair = await ensureLocalApi().server.repairProviderAuthRuntime({ provider: "codex" });
      await ensureLocalApi().server.refreshProviders();
      if (repair.status !== "repaired") {
        setAuthSession({
          id: `provider-auth:codex:repair:${Date.now()}` as ProviderAuthSession["id"],
          provider: "codex",
          state: "failed",
          issue: "provider_cli_missing",
          startedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          message: repair.message,
        });
        return;
      }
      await startCodexAuth();
    } catch (error) {
      toastManager.add({
        type: "error",
        title: "Could not repair Codex runtime",
        description:
          error instanceof Error
            ? error.message
            : "Refresh provider status, then try ChatGPT sign-in again.",
      });
    } finally {
      setIsRepairingAuthRuntime(false);
    }
  }, [startCodexAuth]);

  const disconnectCodexAuth = useCallback(async () => {
    setIsDisconnectingAuth(true);
    try {
      const payload = await ensureLocalApi().server.disconnectProviderAuth({ provider: "codex" });
      onAccessModeChange("workspace");
      applyProvidersUpdated(payload);
      onAuthCompleted();
      setIsAuthDialogOpen(false);
      toastManager.add({
        type: "success",
        title: "ChatGPT account disconnected",
        description: "Forge is using workspace access for Codex.",
      });
    } catch (error) {
      toastManager.add({
        type: "error",
        title: "Could not disconnect ChatGPT",
        description: error instanceof Error ? error.message : "Try again from Settings.",
      });
    } finally {
      setIsDisconnectingAuth(false);
    }
  }, [onAccessModeChange, onAuthCompleted]);

  // FOR-2605 follow-up — a linked account, once added, previously had no way
  // to be removed: the CMMD DELETE route existed and was tested, but nothing
  // in the client called it. Scoped to genuinely-linked rows only (see
  // ProviderAccountManagePanel's isLinkedAccountRow) — the primary connection
  // keeps using disconnectCodexAuth above.
  const removeLinkedCodexAccount = useCallback(
    async (id: string) => {
      const numericId = Number.parseInt(id.replace(/^linked:/, ""), 10);
      if (!Number.isInteger(numericId) || numericId <= 0) {
        toastManager.add({
          type: "error",
          title: "Couldn't remove account",
          description: "This account's id looked wrong. Reload Settings and try again.",
        });
        return;
      }
      setRemovingAccountId(id);
      try {
        await cmmdApiClient.removeLinkedProviderAccount(numericId);
        void queryClient.invalidateQueries({ queryKey: ["cmmd", "providers", "accounts"] });
        toastManager.add({ type: "success", title: "Account removed" });
      } catch (error) {
        toastManager.add({
          type: "error",
          title: "Couldn't remove account",
          description: error instanceof Error ? error.message : "Try again from Settings.",
        });
      } finally {
        setRemovingAccountId(null);
      }
    },
    [queryClient],
  );

  const selectCodexDefaultAccount = useCallback(
    async (account: ProviderAccountListItem) => {
      if (account.isDefaultForNewChats || account.needsReconnect) return;
      setSelectingCodexDefaultAccountId(account.id);
      try {
        const inventory = await cmmdApiClient.setProviderAccountDefault({
          provider: "codex",
          providerAccountId: account.id,
        });
        queryClient.setQueryData(providerAccountsQueryKey, inventory);
        onAccessModeChange("personal");
        await queryClient.invalidateQueries({ queryKey: providerAccountsQueryKey });
        toastManager.add({
          type: "success",
          title: "Default ChatGPT account updated",
          description: "New Codex chats will use the selected account.",
        });
      } catch (error) {
        toastManager.add({
          type: "error",
          title: "Could not update ChatGPT account",
          description: error instanceof Error ? error.message : "Try again from Settings.",
        });
      } finally {
        setSelectingCodexDefaultAccountId(null);
      }
    },
    [onAccessModeChange, queryClient],
  );

  const cancelCodexAuth = useCallback(
    async (options: { closeAfterCancel?: boolean } = {}) => {
      if (!authSession || isProviderAuthSessionTerminal(authSession)) {
        setIsReconnecting(false);
        setIsAuthDialogOpen(false);
        return;
      }
      let cancelled = false;
      try {
        const nextSession = await ensureLocalApi().server.cancelProviderAuth({
          sessionId: authSession.id,
        });
        setAuthSession(nextSession);
        cancelled = true;
      } catch (error) {
        toastManager.add({
          type: "error",
          title: "Could not cancel sign-in",
          description: error instanceof Error ? error.message : "Failed to cancel ChatGPT sign-in.",
        });
        // Leave dialog open so the user can retry or dismiss manually.
      }
      if (cancelled && options.closeAfterCancel) setIsAuthDialogOpen(false);
    },
    [authSession],
  );

  useEffect(() => {
    if (!authSession?.deviceUrl || authSession.state !== "waiting_for_user") return;
    if (openedAuthSessionRef.current === authSession.id) return;
    openedAuthSessionRef.current = authSession.id;
    setAuthBrowserError(null);
    void openCodexAuthInBrowser(authSession.deviceUrl).catch((error) => {
      setAuthBrowserError(
        error instanceof Error ? error.message : "Forge could not open the sign-in page.",
      );
    });
  }, [authSession?.deviceUrl, authSession?.id, authSession?.state, openCodexAuthInBrowser]);

  useEffect(() => {
    const telemetry = getProviderAuthFailureTelemetry(authSession);
    const sessionId = authSession?.id;
    if (!telemetry || !sessionId || reportedAuthFailureSessionRef.current === sessionId) return;
    reportedAuthFailureSessionRef.current = sessionId;
    captureWebError(telemetry.error, telemetry.context);
  }, [authSession]);

  useEffect(() => {
    if (!authSession || isProviderAuthSessionTerminal(authSession)) return;
    let consecutiveErrors = 0;
    const MAX_CONSECUTIVE_ERRORS = 5;
    const interval = window.setInterval(() => {
      void ensureLocalApi()
        .server.getProviderAuthSession({ sessionId: authSession.id })
        .then((nextSession) => {
          consecutiveErrors = 0;
          setAuthSession(nextSession);
          if (nextSession.state === "completed" && !hasHandledAuthCompletionRef.current) {
            hasHandledAuthCompletionRef.current = true;
            // A switch/reconnect is over — the refreshed provider now carries the
            // new account, so hand the dialog back to the connected panel.
            setIsReconnecting(false);
            // "linked" (see startReconnectCodexAuth's credentialTarget) is
            // additive by design — it must never touch the primary
            // access-mode setting, which belongs to whichever connection is
            // already primary.
            if (reconnectTarget === "primary") {
              onAccessModeChange("personal");
            }
            onAuthCompleted();
            if (nextSession.provider === "codex") {
              if (reconnectTarget === "linked") {
                // Unlike "primary" (below), the cloud POST IS the entire
                // feature for a linked account — there is no local benefit
                // to fall back on. Await it and report failure honestly
                // instead of a fire-and-forget best-effort that would leave
                // the dialog claiming success with nothing actually stored
                // (mirrors useProviderAuth.ts's Claude/onboarding path).
                void ensureLocalApi()
                  .server.syncPersonalCodexToCloud({ target: "linked" })
                  .catch(() => ({ status: "failed" as const }))
                  .then((result) => {
                    if (result.status === "synced") {
                      toastManager.add({
                        type: "success",
                        title: "ChatGPT account linked",
                        description: "Forge will keep this account available to switch to.",
                      });
                      void queryClient.invalidateQueries({
                        queryKey: ["cmmd", "providers", "accounts"],
                      });
                    } else {
                      toastManager.add({
                        type: "error",
                        title: "Couldn't link ChatGPT account",
                        description:
                          "The sign-in worked, but Forge couldn't save this account. Try again.",
                      });
                    }
                  });
              } else {
                // Publish the just-connected personal Codex credential to the
                // user's cloud runtimes (card 1359 path A). Best-effort: the
                // local connection already succeeded, so a sync failure must
                // not disrupt this flow.
                void ensureLocalApi()
                  .server.syncPersonalCodexToCloud({ target: "primary" })
                  .catch(() => undefined);
              }
            }
          }
        })
        .catch((error) => {
          consecutiveErrors += 1;
          if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
            window.clearInterval(interval);
            toastManager.add({
              type: "error",
              title: "Sign-in check failed",
              description:
                error instanceof Error
                  ? error.message
                  : "Could not reach Forge. Dismiss and retry ChatGPT sign-in.",
            });
          }
        });
    }, 1_000);
    return () => window.clearInterval(interval);
  }, [authSession, onAccessModeChange, onAuthCompleted, reconnectTarget, queryClient]);

  return (
    <div className="min-w-0">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-medium text-foreground">Accounts</div>
        </div>
        {hasManageableCodexAccount ? (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 shrink-0 px-2 text-xs text-muted-foreground hover:text-foreground"
            aria-label="Manage ChatGPT accounts"
            onClick={() => {
              setIsReconnecting(false);
              setIsAuthDialogOpen(true);
            }}
          >
            Manage
          </Button>
        ) : null}
      </div>

      <div role="radiogroup" aria-label="Codex access source" className="divide-y divide-border/60">
        {workspaceWalletAllowed ? (
          <button
            type="button"
            role="radio"
            aria-checked={effectiveIsUsingWorkspacePlan}
            disabled={!isWorkspaceOnline}
            onClick={() => onAccessModeChange("workspace")}
            className={cn(
              "flex min-h-14 w-full items-center justify-between gap-3 py-2.5 text-left transition-colors",
              isWorkspaceOnline
                ? "cursor-pointer hover:bg-muted/30"
                : "cursor-not-allowed opacity-60",
            )}
          >
            <div className="flex min-w-0 items-start gap-3">
              <span
                aria-hidden="true"
                className={cn(
                  "mt-0.5 flex size-3.5 shrink-0 items-center justify-center rounded-full border",
                  effectiveIsUsingWorkspacePlan ? "border-success" : "border-border",
                )}
              >
                {effectiveIsUsingWorkspacePlan ? (
                  <span className="size-1.5 rounded-full bg-success" />
                ) : null}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium leading-5 text-foreground">
                  CMMD workspace
                </div>
                <div className="text-xs leading-5 text-muted-foreground">
                  {effectiveIsUsingWorkspacePlan ? "Default account" : workspaceAccessDescription}
                </div>
              </div>
            </div>
          </button>
        ) : null}

        {showInlineCodexAccounts ? (
          <div>
            <ProviderAccountSelector
              accounts={codexAccountRows}
              defaultScopeLabel={defaultScopeLabel}
              disabled={selectingCodexDefaultAccountId !== null}
              onSelectDefault={(account) => void selectCodexDefaultAccount(account)}
              role="presentation"
            />
          </div>
        ) : (
          <div className="flex min-h-14 items-center justify-between gap-3 py-2.5 transition-colors hover:bg-muted/20">
            <button
              type="button"
              role="radio"
              aria-checked={effectiveIsUsingChatGptAccount || !workspaceWalletAllowed}
              onClick={() => {
                if (hasUsableCodexProviderAccount || hasUserCodexAccount) {
                  onAccessModeChange("personal");
                  return;
                }
                if (hasManageableCodexAccount) {
                  void startReconnectCodexAuth({ selectPersonalOnSuccess: true });
                  return;
                }
                void startCodexAuth();
              }}
              className="flex min-w-0 flex-1 cursor-pointer items-start gap-3 text-left"
            >
              <span
                aria-hidden="true"
                className={cn(
                  "mt-0.5 flex size-3.5 shrink-0 items-center justify-center rounded-full border",
                  effectiveIsUsingChatGptAccount || !workspaceWalletAllowed
                    ? "border-success"
                    : "border-border",
                )}
              >
                {effectiveIsUsingChatGptAccount || !workspaceWalletAllowed ? (
                  <span className="size-1.5 rounded-full bg-success" />
                ) : null}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium leading-5 text-foreground">
                  Personal ChatGPT
                </div>
                <div className="truncate text-xs leading-5 text-muted-foreground">
                  {hasManageableCodexAccount
                    ? [
                        maskConnectedAccountLabel(codexAccountEmail),
                        codexPlanLabel,
                        effectiveIsUsingChatGptAccount || !workspaceWalletAllowed
                          ? "Default"
                          : null,
                      ]
                        .filter((value): value is string => value !== null)
                        .join(" · ")
                    : personalAccessDescription}
                </div>
              </div>
            </button>
            <div className="flex shrink-0 items-center gap-1.5">
              {!hasManageableCodexAccount ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-xs"
                  disabled={isStartingAuth}
                  onClick={() => void startCodexAuth()}
                >
                  {isStartingAuth ? <LoaderIcon className="animate-spin" /> : null}
                  Connect
                </Button>
              ) : null}
            </div>
          </div>
        )}
      </div>

      <Dialog
        open={isAuthDialogOpen}
        onOpenChange={(open) => {
          if (open) {
            setIsAuthDialogOpen(true);
            return;
          }
          if (isAuthSessionActive) {
            void cancelCodexAuth({ closeAfterCancel: true });
            return;
          }
          // Abandoning a switch must not leave the dialog stuck on sign-in the
          // next time it opens — the old account is still connected.
          if (isCodexCloudSwitching) {
            cloudSignIn.cancel();
          }
          setIsReconnecting(false);
          setIsAuthDialogOpen(false);
        }}
      >
        <DialogPopup className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {isManagingConnectedAccount ? "ChatGPT accounts" : "Sign in with ChatGPT"}
            </DialogTitle>
            <DialogDescription>
              {isManagingConnectedAccount
                ? "Choose the account Codex uses for new chats."
                : "Forge uses OpenAI device authorization because Codex runs inside your Forge project."}
            </DialogDescription>
          </DialogHeader>
          <DialogPanel className="space-y-3">
            {isManagingConnectedAccount ? (
              <ProviderAccountManagePanel
                accounts={codexAccountRows}
                accountEmail={codexAccountEmail}
                planLabel={codexPlanLabel}
                scopeLabel={
                  codexAccountRowsAreCloudBacked
                    ? "Stored in your CMMD account. Available to your cloud runtimes."
                    : codexCredentialScopeLabel
                }
                isDefaultForNewChats={isUsingChatGptAccount}
                defaultScopeLabel={defaultScopeLabel}
                needsReconnect={needsReconnect}
                isSwitching={isStartingAuth || cloudSignIn.busy}
                isDisconnecting={isDisconnectingAuth || cloudSignIn.disconnecting}
                onAddAccount={() => void startReconnectCodexAuth({ credentialTarget: "linked" })}
                onReconnectAccount={() =>
                  void startReconnectCodexAuth({ credentialTarget: "primary" })
                }
                onDisconnect={() => {
                  // On a cloud surface the credential lives in the user store,
                  // not the local CLI — remove it there and refresh the shared
                  // connection so the selector reverts to "Connect".
                  if (disconnectsCodexCloudStore) {
                    void cloudSignIn
                      .disconnect()
                      .then(() => {
                        onAccessModeChange("workspace");
                        setIsAuthDialogOpen(false);
                      })
                      .catch((error: unknown) => {
                        toastManager.add({
                          type: "error",
                          title: "Could not disconnect ChatGPT",
                          description:
                            error instanceof Error ? error.message : "Try again from Settings.",
                        });
                      });
                    return;
                  }
                  void disconnectCodexAuth();
                }}
                onRemoveAccount={(id) => void removeLinkedCodexAccount(id)}
                removingAccountId={removingAccountId}
              />
            ) : isCodexCloudSwitching || useCodexCloudSignIn ? (
              <CloudProviderSignInView
                controller={cloudSignIn}
                provider="codex"
                connectedMode="manage"
                onCancel={() => setIsReconnecting(false)}
              />
            ) : isAuthFailed ? (
              <>
                <div className="rounded-md border border-destructive-border bg-destructive-surface p-3">
                  <div className="text-sm font-medium text-destructive">
                    {authFailureViewModel.title}
                  </div>
                  <div className="mt-1 text-sm leading-5 text-destructive/90">
                    {authFailureViewModel.detail}
                  </div>
                </div>
                <div className="rounded-md border border-border/70 bg-muted/30 p-3">
                  <div className="flex items-start gap-2">
                    <InfoIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 text-xs leading-5 text-muted-foreground">
                      <div className="font-medium text-foreground">
                        {authFailureViewModel.guidanceTitle}
                      </div>
                      <div>{authFailureViewModel.guidance}</div>
                      {authFailureViewModel.action === "security_settings" ? (
                        <Button
                          className="mt-2 h-7 px-2 text-xs"
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            void openCodexAuthInBrowser(CODEX_DEVICE_AUTH_HELP_URL).catch((error) =>
                              setAuthBrowserError(
                                error instanceof Error ? error.message : String(error),
                              ),
                            )
                          }
                        >
                          <ExternalLinkIcon />
                          Open Security Settings
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
                {authFailureViewModel.action === "none" ? (
                  authFailureViewModel.kind === "codex_unsupported_surface" ? (
                    // Card 1359: this surface can't run codex login locally — offer
                    // the cloud auth-VM flow instead (flag-gated). Shares the
                    // selector's controller so connecting here updates the selector.
                    isCloudProviderSignInEnabled() ? (
                      <CloudProviderSignInView
                        controller={cloudSignIn}
                        provider="codex"
                        connectedMode="manage"
                        onCancel={() => setIsReconnecting(false)}
                      />
                    ) : null
                  ) : null
                ) : (
                  <Button
                    className="w-full"
                    disabled={isRepairingAuthRuntime || isStartingAuth}
                    onClick={() =>
                      void (authFailureViewModel.action === "repair_retry"
                        ? repairAndRetryCodexAuth()
                        : startCodexAuth())
                    }
                  >
                    {isRepairingAuthRuntime || isStartingAuth ? (
                      <LoaderIcon className="animate-spin" />
                    ) : null}
                    {authFailureViewModel.action === "repair_retry"
                      ? "Install Codex and retry"
                      : authSession?.state === "expired"
                        ? "Get new code"
                        : "Try again"}
                  </Button>
                )}
              </>
            ) : readyAuthSession ? (
              <>
                <div className="rounded-md border border-border/70 bg-muted/20 p-3">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-xs font-medium text-muted-foreground">Codex code</div>
                      <div className="mt-0.5 text-[11px] text-muted-foreground/80">
                        Expires in {readyAuthSession.expiresIn ?? "15 minutes"}
                      </div>
                    </div>
                    <Button
                      size="icon-sm"
                      variant="outline"
                      aria-label="Copy Codex sign-in code"
                      onClick={() =>
                        void copyProviderAuthText(readyAuthSession.userCode ?? "", "Code", (code) =>
                          setManualCopyValues((values) => ({ ...values, code })),
                        )
                      }
                    >
                      <CopyIcon />
                    </Button>
                  </div>
                  <code className="block rounded-md border border-border/70 bg-background px-3 py-3 font-mono text-xl font-semibold tracking-[0.16em] text-foreground tabular-nums">
                    {readyAuthSession.userCode}
                  </code>
                  <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs leading-5 text-muted-foreground">
                    {normalizedUserCode ? (
                      <span>
                        Split boxes:{" "}
                        <span className="font-mono font-medium tracking-[0.08em] text-foreground">
                          {normalizedUserCode}
                        </span>
                      </span>
                    ) : null}
                    <span className="text-muted-foreground/60">Device authorization required</span>
                  </div>
                  {manualCopyValues.code ? (
                    <div className="mt-3 rounded-md border border-border/70 bg-muted/30 p-3">
                      <div className="text-xs font-medium text-foreground">Copy code manually</div>
                      <Textarea
                        readOnly
                        value={manualCopyValues.code}
                        rows={1}
                        className="mt-2 text-xs leading-relaxed"
                        onFocus={(event) => event.currentTarget.select()}
                        onClick={(event) => event.currentTarget.select()}
                      />
                    </div>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <div className="text-xs font-medium text-foreground">Next</div>
                  {[
                    "Open the OpenAI sign-in page.",
                    "Choose the ChatGPT account Forge should use.",
                    "Enter the code, then finish MFA if OpenAI asks.",
                    "Approve access. Forge switches after Codex confirms login.",
                  ].map((step, index) => (
                    <div
                      key={step}
                      className="grid grid-cols-[20px_1fr] items-start gap-2 text-xs leading-5 text-muted-foreground"
                    >
                      <span className="flex size-5 items-center justify-center rounded-sm border border-border/70 bg-muted/30 font-mono text-[10px] text-muted-foreground">
                        {index + 1}
                      </span>
                      <span>{step}</span>
                    </div>
                  ))}
                </div>
                {authBrowserError ? (
                  <div className="rounded-md border border-destructive-border bg-destructive-surface p-3 text-xs leading-5 text-destructive">
                    Forge could not open the sign-in page automatically. Use the button below or
                    copy the link.
                    <div className="mt-1 text-destructive/90">{authBrowserError}</div>
                  </div>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  <Button
                    render={
                      readyAuthSession.deviceUrl ? (
                        <a href={readyAuthSession.deviceUrl} rel="noreferrer" />
                      ) : undefined
                    }
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (!readyAuthSession.deviceUrl) return;
                      void openCodexAuthFromUserAction(readyAuthSession.deviceUrl).catch((error) =>
                        setAuthBrowserError(error instanceof Error ? error.message : String(error)),
                      );
                    }}
                  >
                    <ExternalLinkIcon />
                    Open sign-in page
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      void copyProviderAuthText(readyAuthSession.deviceUrl ?? "", "Link", (link) =>
                        setManualCopyValues((values) => ({ ...values, link })),
                      )
                    }
                  >
                    <CopyIcon />
                    Copy link
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      void (async () => {
                        if (authSession && !isProviderAuthSessionTerminal(authSession)) {
                          await ensureLocalApi().server.cancelProviderAuth({
                            sessionId: authSession.id,
                          });
                        }
                        setAuthSession(null);
                        await startCodexAuth();
                      })()
                    }
                  >
                    <RefreshCwIcon />
                    New code
                  </Button>
                </div>
                <div className="flex items-start gap-2 rounded-md border border-border/70 bg-muted/20 p-3 text-xs leading-5 text-muted-foreground">
                  <InfoIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    Some ChatGPT accounts block device-code sign-in until it is enabled in Security
                    Settings.
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <LoaderIcon className="size-3 animate-spin" />
                  Waiting for Codex CLI to confirm sign-in.
                </div>
                {manualCopyValues.link ? (
                  <div className="rounded-md border border-border/70 bg-muted/30 p-3">
                    <div className="text-xs font-medium text-foreground">Copy link manually</div>
                    <Textarea
                      readOnly
                      value={manualCopyValues.link}
                      rows={manualCopyValues.link.length > 32 ? 3 : 1}
                      className="mt-2 text-xs leading-relaxed"
                      onFocus={(event) => event.currentTarget.select()}
                      onClick={(event) => event.currentTarget.select()}
                    />
                  </div>
                ) : null}
              </>
            ) : isAuthVerifying ? (
              <div className="rounded-md border border-border/70 bg-muted/30 p-3">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <LoaderIcon className="size-4 animate-spin" />
                  Verifying Codex account
                </div>
                <div className="mt-1 text-sm leading-5 text-muted-foreground">
                  Forge is refreshing provider status before switching this card to your Codex
                  account.
                </div>
              </div>
            ) : (
              <div className="rounded-md border border-border/70 bg-muted/30 p-3">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <LoaderIcon className="size-4 animate-spin" />
                  Preparing Codex sign-in
                </div>
                <div className="mt-1 text-sm leading-5 text-muted-foreground">
                  {authSession?.message ?? "Starting device authorization in your Forge project."}
                </div>
              </div>
            )}
          </DialogPanel>
        </DialogPopup>
      </Dialog>
    </div>
  );
}
